import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminGrupoService, adminEmpresaService } from '../../shared/services';
import { FolderOpen, Save, X } from 'lucide-react';

interface GrupoFormData {
  nome: string;
  descricao: string;
  cor: string;
  ordem: number;
}

const CORES_DISPONIVEIS = [
  { value: 'blue', label: 'Azul', classe: 'bg-blue-500' },
  { value: 'green', label: 'Verde', classe: 'bg-green-500' },
  { value: 'purple', label: 'Roxo', classe: 'bg-purple-500' },
  { value: 'orange', label: 'Laranja', classe: 'bg-orange-500' },
  { value: 'red', label: 'Vermelho', classe: 'bg-red-500' },
  { value: 'yellow', label: 'Amarelo', classe: 'bg-yellow-500' },
  { value: 'pink', label: 'Rosa', classe: 'bg-pink-500' },
  { value: 'indigo', label: 'Índigo', classe: 'bg-indigo-500' },
];

export function AdminGrupoFormPage() {
  const { empresaId, grupoId } = useParams<{ empresaId: string; grupoId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!grupoId;

  const [formData, setFormData] = useState<GrupoFormData>({
    nome: '',
    descricao: '',
    cor: 'blue',
    ordem: 1,
  });

  const { data: empresa } = useQuery({
    queryKey: ['admin-empresa', empresaId],
    queryFn: () => adminEmpresaService.getEmpresa(empresaId!),
    enabled: !!empresaId,
  });

  const { data: grupo } = useQuery({
    queryKey: ['admin-grupo', grupoId],
    queryFn: () => adminGrupoService.getGrupo(grupoId!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (grupo) {
      setFormData({
        nome: grupo.nome || '',
        descricao: grupo.descricao || '',
        cor: grupo.cor || 'blue',
        ordem: grupo.ordem || 1,
      });
    }
  }, [grupo]);

  const createMutation = useMutation({
    mutationFn: (data: GrupoFormData) => {
      console.log('🚀 [AdminGrupoFormPage] Criando grupo');
      console.log('📊 [AdminGrupoFormPage] EmpresaId:', empresaId);
      console.log('📦 [AdminGrupoFormPage] Dados do grupo:', JSON.stringify(data, null, 2));
      return adminGrupoService.createGrupo(empresaId!, data);
    },
    onSuccess: (result) => {
      console.log('✅ [AdminGrupoFormPage] Grupo criado com sucesso:', result);
      queryClient.invalidateQueries({ queryKey: ['admin-grupos', empresaId] });
      navigate(`/admin/empresas/${empresaId}/grupos`);
    },
    onError: (error: any) => {
      console.error('❌ [AdminGrupoFormPage] Erro ao criar grupo:', error);
      console.error('❌ [AdminGrupoFormPage] Response:', error.response?.data);
      console.error('❌ [AdminGrupoFormPage] Status:', error.response?.status);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: GrupoFormData) => adminGrupoService.updateGrupo(grupoId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-grupos', empresaId] });
      queryClient.invalidateQueries({ queryKey: ['admin-grupo', grupoId] });
      navigate(`/admin/empresas/${empresaId}/grupos`);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'ordem' ? parseInt(value) || 1 : value
    }));
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <FolderOpen className="w-8 h-8 text-blue-600" />
            {isEdit ? 'Editar Grupo' : 'Novo Grupo'}
          </h1>
          <p className="text-gray-600 mt-2">
            {empresa?.nome ? `Empresa: ${empresa.nome}` : 'Configure o grupo de trilhas'}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                Nome do Grupo *
              </label>
              <input
                type="text"
                id="nome"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: Liderança, Vendas, TI, RH..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Ex: "Liderança", "Vendas", "TI", "RH", "Onboarding"
              </p>
            </div>

            {/* Descrição */}
            <div>
              <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                id="descricao"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descreva o objetivo deste grupo..."
              />
            </div>

            {/* Cor */}
            <div>
              <label htmlFor="cor" className="block text-sm font-medium text-gray-700 mb-2">
                Cor do Grupo
              </label>
              <div className="grid grid-cols-4 gap-3">
                {CORES_DISPONIVEIS.map((cor) => (
                  <button
                    key={cor.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, cor: cor.value }))}
                    className={`flex items-center gap-2 px-4 py-3 border-2 rounded-lg transition-all ${
                      formData.cor === cor.value
                        ? 'border-gray-900 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full ${cor.classe}`}></div>
                    <span className="text-sm font-medium">{cor.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Ordem */}
            <div>
              <label htmlFor="ordem" className="block text-sm font-medium text-gray-700 mb-2">
                Ordem de Exibição
              </label>
              <input
                type="number"
                id="ordem"
                name="ordem"
                value={formData.ordem}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Grupos com menor ordem aparecem primeiro
              </p>
            </div>
          </div>

          {/* Botões */}
          <div className="flex items-center gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              {isEdit ? 'Salvar Alterações' : 'Criar Grupo'}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/admin/empresas/${empresaId}/grupos`)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="w-5 h-5" />
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
